import { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import IssuerSignerPicker from '../../components/IssuerSignerPicker';
import { useMyDIDs } from '../../api/hooks';
import { issueSignedDelegation, type IssuerSigner } from '../../lib/credentialSigning';
import { upi, rupees, INSTRUMENT_LABELS, type Attestation } from '../../api/upi';

interface Props {
  open: boolean;
  /** The agent receiving the delegation. */
  agentDid: string;
  onClose: () => void;
  onIssued: () => void;
}

const EXAMPLE_CONSTRAINTS = `{
  "currency": "INR",
  "maxPerTxnPaise": 150000,
  "maxPerDayPaise": 300000,
  "allowedMerchantVPAs": ["freshmart@examplebank"]
}`;

/**
 * Grant an agent authority with a signed delegation. The delegator signs it
 * (MetaMask or its Ed25519 key); a sub-delegation names its parent and may
 * only narrow it, which the API checks.
 */
export default function NewDelegationDialog({ open, agentDid, onClose, onIssued }: Props) {
  const { data: myDIDs } = useMyDIDs();
  // Personal DIDs first: an agent's own DID delegates only to sub-agents.
  const activeDIDs = (myDIDs ?? []).filter((d) => d.status === 'active')
    .sort((a, b) => Number(a.did.startsWith('did:adi:agent:')) - Number(b.did.startsWith('did:adi:agent:')));
  const [delegatorDid, setDelegatorDid] = useState('');
  const [scope, setScope] = useState('');
  const [constraints, setConstraints] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [maxDepth, setMaxDepth] = useState('0');
  const [parentId, setParentId] = useState('');
  const [signer, setSigner] = useState<IssuerSigner | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attestations, setAttestations] = useState<Attestation[]>([]);
  const [attestationId, setAttestationId] = useState('');

  const delegator = delegatorDid || activeDIDs[0]?.did || '';
  const today = new Date().toISOString().slice(0, 10);
  // Payment authority must be bound to a bank instrument the bank attested.
  const paysUPI = !parentId.trim() && scope.split(',').map((s) => s.trim()).some((s) => s === 'upi-pay' || s === '*');
  useEffect(() => {
    if (!open || !paysUPI || !delegator) return;
    upi.attestations(agentDid, delegator)
      .then((list) => setAttestations(list.filter((a) => a.usable)))
      .catch(() => setAttestations([]));
  }, [open, paysUPI, agentDid, delegator]);
  const attestation = attestations.find((a) => a.id === attestationId);

  const submit = async () => {
    setError('');
    const scopeList = scope.split(',').map((s) => s.trim()).filter(Boolean);
    if (scopeList.length === 0) {
      setError('List at least one capability, e.g. payments.upi');
      return;
    }
    let parsed: Record<string, unknown> | undefined;
    if (constraints.trim()) {
      try {
        parsed = JSON.parse(constraints);
      } catch {
        setError('Constraints must be a JSON object.');
        return;
      }
    }
    if (!signer) {
      setError('Choose how the delegator signs.');
      return;
    }
    if (paysUPI && !attestation) {
      setError('Payment authority (upi-pay) must draw on a bank instrument. Choose one, or set one up under UPI Payments.');
      return;
    }
    setBusy(true);
    try {
      await issueSignedDelegation({
        delegatorDid: delegator,
        delegateDid: agentDid,
        scope: scopeList,
        constraints: parsed,
        validUntil: validUntil ? new Date(`${validUntil}T23:59:59Z`).toISOString() : undefined,
        maxDepth: Number(maxDepth) || 0,
        parentDelegationId: parentId.trim() || undefined,
        instrument: paysUPI && attestation ? { kind: attestation.kind, ref: attestation.instrumentRef, attestation: attestation.id } : undefined,
      }, signer);
      onIssued();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { if (!busy) onClose(); }} title="New delegation">
      <div className="space-y-4 text-sm">
        <p className="text-text-muted">
          A delegation is a credential the delegator signs. The agent can act only within its scope and constraints, and
          anything it delegates on must be narrower still.
        </p>

        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Delegator</span>
          <select
            id="delegation-delegator"
            value={delegator}
            onChange={(e) => setDelegatorDid(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          >
            {activeDIDs.map((d) => <option key={d.did} value={d.did}>{d.did}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Scope (comma-separated capabilities)</span>
          <input
            id="delegation-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder="upi-pay"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs"
          />
        </label>

        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Constraints (JSON, optional)</span>
          <textarea
            id="delegation-constraints"
            value={constraints}
            onChange={(e) => setConstraints(e.target.value)}
            rows={5}
            placeholder={EXAMPLE_CONSTRAINTS}
            spellCheck={false}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </label>

        {paysUPI && (
          <label className="block">
            <span className="text-xs font-semibold text-text-muted">Bank instrument (required for upi-pay)</span>
            <select id="delegation-instrument" value={attestationId} onChange={(e) => setAttestationId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs">
              <option value="">{attestations.length ? 'Choose…' : 'None attested for this agent and delegator yet'}</option>
              {attestations.map((a) => (
                <option key={a.id} value={a.id}>
                  {INSTRUMENT_LABELS[a.kind] ?? a.kind} · {a.instrumentRef} · up to {rupees(a.limits.maxPerTxnPaise)} a payment
                </option>
              ))}
            </select>
            <span className="text-[11px] text-text-faint">The constraints must be no looser than the bank's limits, in whole paise, currency INR.</span>
          </label>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-semibold text-text-muted">Valid until (default 24 hours)</span>
            <input id="delegation-valid-until" type="date" min={today} value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-text-muted">Further delegation allowed (hops)</span>
            <input id="delegation-max-depth" type="number" min={0} max={7} value={maxDepth} onChange={(e) => setMaxDepth(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs" />
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Parent delegation id (only for a sub-delegation)</span>
          <input id="delegation-parent" value={parentId} onChange={(e) => setParentId(e.target.value)} placeholder="urn:uuid:…"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs" />
        </label>

        <IssuerSignerPicker did={delegator} onChange={setSigner} />

        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="rounded-lg border border-border-subtle px-4 py-2 font-medium text-text-main hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => void submit()} disabled={busy || !signer || !delegator} className="btn-primary disabled:opacity-50">
            {busy ? 'Signing…' : 'Sign and delegate'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
